// chat/actions.ts — the chat control executor (cloned from Oriora Expense's src/chat/actions.ts,
// re-derived for THIS app's own store). The client router (chat/brain.ts) maps ONE user message →
// ONE ChatAction; this file EXECUTES that action against the on-device store (src/store.ts).
// Everything here is LOCAL SQLite over Tauri IPC — no network, no billing. The two AI-billed
// actions (summarize / ask) happen here by calling the EXISTING live endpoints (narrateGarage /
// askGarage) — no new server route, no new prompt.
//
// HITL discipline (mirrors Expense): the destructive actions (delete_fillup / delete_service) are
// NEVER executed from a chat turn on their own — the Chat UI holds them in `pending` and requires
// an explicit Confirm tap first. This executor just performs the delete once the UI hands it back.
// Vehicles are NOT deletable from chat (mirrors Expense's "categories aren't removed from chat" —
// deleting a vehicle destroys its whole fill-up/service history, so it stays on the dedicated
// "Remove this vehicle" screen with its own confirm).

import {
  store, normalizeKind, prettyKind, todayLocal,
  type FillupPatch, type ServicePatch, type NewVehicle, type VehiclePatch, type DistanceUnit, type VolumeUnit,
} from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "garage";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave } from "../brain";
import { addInstruction } from "./brain";
import { narrateGarage, askGarage, BrainError } from "../brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

// A compact row rendered inline in a chat bubble (filter results).
export interface FilterRow {
  id: string;
  kind: "fillup" | "service";
  date_on: string;
  label: string;
  detail: string;
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "add_vehicle"; name: string; make?: string; model?: string; year?: number }
  | { kind: "edit_vehicle"; id: string; patch: VehiclePatch; label?: string }
  | { kind: "delete_vehicle"; id: string; label?: string } // UI confirms before this reaches executeAction (deletes ALL its records)
  | { kind: "add_fillup"; vehicle_id: string; odometer: number; volume: number; cost?: number; is_full_tank?: boolean; note?: string }
  | { kind: "add_service"; vehicle_id: string; kind_name: string; odometer: number; cost?: number; note?: string }
  | { kind: "edit_fillup"; id: string; patch: FillupPatch; label?: string }
  | { kind: "edit_service"; id: string; patch: ServicePatch; label?: string }
  | { kind: "delete_fillup"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "delete_service"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "filter"; vehicle_id: string; entity: "fillup" | "service" | "both"; term?: string; minCost?: number; maxCost?: number; label?: string }
  | { kind: "summarize"; vehicle_id: string }
  | { kind: "ask"; vehicle_id: string; question: string }
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "remember"; instruction: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first

export interface ActionResult {
  ok: boolean;
  message: string;    // shown back in the chat
  changed?: boolean;  // the vehicle/fillup/service log changed → the main screen should refresh
  rows?: FilterRow[]; // filter matches to render inline
}

const money = (n: number) => n.toLocaleString(undefined, { style: "currency", currency: "USD", currencyDisplay: "narrowSymbol" });

function fmtDateShort(ts: string): string {
  return ts.slice(0, 10);
}

export async function executeAction(a: ChatAction, ctx?: { transcript?: string }): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "add_vehicle": {
        const input: NewVehicle = {
          name: a.name,
          make: a.make,
          model: a.model,
          year: a.year,
          distance_unit: "mi" as DistanceUnit,
          volume_unit: "gal" as VolumeUnit,
        };
        const v = await store.createVehicle(input);
        return { ok: true, message: `Added "${v.name}" to your garage.`, changed: true };
      }

      case "edit_vehicle":
        await store.updateVehicle(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the vehicle"}.`, changed: true };

      case "delete_vehicle":
        await store.deleteVehicle(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the vehicle"} and all of its records.`, changed: true };

      case "add_fillup": {
        const f = await store.createFillup({
          vehicle_id: a.vehicle_id,
          odometer: a.odometer,
          volume: a.volume,
          cost: a.cost ?? 0,
          is_full_tank: a.is_full_tank ?? true,
          note: a.note ?? "",
        });
        const costPart = f.cost > 0 ? ` · ${money(f.cost)}` : "";
        return { ok: true, message: `Logged a fill-up — ${f.volume} at ${f.odometer}${costPart}.`, changed: true };
      }

      case "add_service": {
        const s = await store.createService({
          vehicle_id: a.vehicle_id,
          kind: normalizeKind(a.kind_name),
          odometer: a.odometer,
          cost: a.cost ?? 0,
          note: a.note ?? "",
        });
        const costPart = s.cost > 0 ? ` · ${money(s.cost)}` : "";
        return { ok: true, message: `Logged ${prettyKind(s.kind)} at ${s.odometer}${costPart}.`, changed: true };
      }

      case "edit_fillup":
        await store.updateFillup(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the fill-up"}.`, changed: true };

      case "edit_service":
        await store.updateService(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the service entry"}.`, changed: true };

      case "delete_fillup":
        await store.deleteFillup(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the fill-up"}.`, changed: true };

      case "delete_service":
        await store.deleteService(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the service entry"}.`, changed: true };

      case "filter": {
        const wantFillups = a.entity === "fillup" || a.entity === "both";
        const wantServices = a.entity === "service" || a.entity === "both";
        const [fillups, services] = await Promise.all([
          wantFillups ? store.listFillups(a.vehicle_id, 50000) : Promise.resolve([]),
          wantServices ? store.listServices(a.vehicle_id, 50000) : Promise.resolve([]),
        ]);
        const term = a.term?.toLowerCase().trim();
        const inCost = (c: number) =>
          (a.minCost === undefined || c >= a.minCost) && (a.maxCost === undefined || c <= a.maxCost);

        const fillupRows: FilterRow[] = fillups
          .filter((f) => inCost(f.cost) && (!term || f.note.toLowerCase().includes(term)))
          .map((f) => ({
            id: f.id, kind: "fillup" as const, date_on: fmtDateShort(f.ts),
            label: `${f.volume} ${a.entity === "both" ? "fill-up" : ""}`.trim(),
            detail: `${f.odometer}${f.cost > 0 ? ` · ${money(f.cost)}` : ""}`,
          }));
        const serviceRows: FilterRow[] = services
          .filter((s) => inCost(s.cost) && (!term || s.kind.toLowerCase().includes(term) || s.note.toLowerCase().includes(term)))
          .map((s) => ({
            id: s.id, kind: "service" as const, date_on: fmtDateShort(s.ts),
            label: prettyKind(s.kind),
            detail: `${s.odometer}${s.cost > 0 ? ` · ${money(s.cost)}` : ""}`,
          }));

        const rows = [...fillupRows, ...serviceRows].sort((x, y) => (x.date_on < y.date_on ? 1 : -1));
        const total = rows.length;
        const forLabel = a.label ? ` for "${a.label}"` : "";
        return {
          ok: true,
          message: `${total} match${total !== 1 ? "es" : ""}${forLabel}` + (total > 50 ? " (showing first 50)" : ""),
          rows: rows.slice(0, 50),
        };
      }

      case "summarize": {
        const signals = await store.computeSignals(a.vehicle_id, todayLocal());
        const r = await narrateGarage(signals);
        return { ok: true, message: r.narration || "Not enough logged yet to narrate." };
      }

      case "ask": {
        const signals = await store.computeSignals(a.vehicle_id, todayLocal());
        const r = await askGarage(signals, a.question);
        return { ok: true, message: r.answer || "I couldn't find that in your logged data." };
      }

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // A backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path. One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my garage" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large, use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_to_cloud": {
        // Saves the CURRENT conversation (or a note) to the customer's OWN cloud — distinct from
        // backup_save (the whole collection). Text relay only; Oriora retains nothing.
        const content = a.contentKind === "conversation" ? (ctx?.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}
